/* eslint-disable react-refresh/only-export-components */
/**
 * Network Context
 * Provides reactive network status to the entire application.
 *
 * Responsibilities:
 * 1. Detects online/offline transitions.
 * 2. Triggers reference-data sync on reconnect.
 * 3. Triggers sync-queue replay on reconnect and exposes pending count.
 * 4. Provides toast-style notification state for sync events.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNetworkStatus, type NetworkQuality } from '../hooks/useNetworkStatus';
import { hasSession, onTokensChanged } from '../services/authToken';
import { syncAll } from '../services/referenceDataSync';
import { processQueue, getPendingCount } from '../services/syncQueue';

import type { ReactNode } from 'react';

/** Resolve after `ms` milliseconds. */
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Cap on replay passes per trigger — a backstop against a tight retry loop. */
const MAX_REPLAY_PASSES = 25;

/** How often to retry draining the queue automatically while online (ms). */
const AUTO_SYNC_INTERVAL_MS = 20_000;

// ─── Notification type ─────────────────────────────────────────────────────────

export type SyncNotificationSeverity = 'info' | 'success' | 'warning' | 'error';

export interface SyncNotification {
  message: string;
  severity: SyncNotificationSeverity;
  /** i18n key (optional) – passed to t() by the consuming component */
  i18nKey?: string;
  i18nValues?: Record<string, unknown>;
}

// ─── Context type ──────────────────────────────────────────────────────────────

interface NetworkContextType {
  /** Whether the browser currently has network connectivity */
  isOnline: boolean;
  /** Whether the app was offline at any point during this session */
  wasOffline: boolean;
  /** Timestamp of the last time the app was online */
  lastOnlineAt: Date | null;
  /** Assessed network quality: 'offline' | 'poor' | 'fair' | 'good' */
  networkQuality: NetworkQuality;
  /** Effective connection type from browser API (e.g. '4g', '3g', '2g') */
  effectiveType: string | null;
  /** Last measured ping to the backend API in ms */
  pingMs: number | null;
  /** Whether the backend API is actually reachable */
  isApiReachable: boolean;
  /** Number of pending items in the sync queue */
  pendingSyncCount: number;
  /** Whether a reference data sync is currently running */
  isSyncing: boolean;
  /** Whether the sync queue is currently being replayed */
  isProcessingQueue: boolean;
  /** Last sync-queue notification (cleared by calling clearSyncNotification) */
  syncNotification: SyncNotification | null;
  /** Dismiss the current syncNotification */
  clearSyncNotification: () => void;
  /** Trigger a manual sync of reference data */
  triggerSync: () => Promise<void>;
  /** Trigger a manual replay of the sync queue */
  triggerQueueReplay: () => Promise<void>;
}

// ─── Context ───────────────────────────────────────────────────────────────────

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

// ─── Provider ──────────────────────────────────────────────────────────────────

export function NetworkProvider({ children }: { children: ReactNode }) {
  const {
    isOnline,
    wasOffline,
    lastOnlineAt,
    networkQuality,
    effectiveType,
    pingMs,
    isApiReachable,
  } = useNetworkStatus();

  const [isSyncing, setIsSyncing] = useState(false);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [syncNotification, setSyncNotification] = useState<SyncNotification | null>(null);

  // Refs mirror frequently-changing values so long-lived intervals/listeners can
  // read the latest state without being torn down and recreated on every change.
  const prevReachableRef = useRef(isOnline && isApiReachable);
  const isSyncingRef = useRef(false);
  const isProcessingQueueRef = useRef(false);
  const pendingCountRef = useRef(0);
  /** True when the refresh token is dead — suppresses auto-retry until re-auth. */
  const needsReauthRef = useRef(false);

  useEffect(() => {
    pendingCountRef.current = pendingSyncCount;
  }, [pendingSyncCount]);

  const clearSyncNotification = useCallback(() => setSyncNotification(null), []);

  // ── Reference data sync ───────────────────────────────────────────────────

  const triggerSync = useCallback(async () => {
    if (isSyncingRef.current) {return;}
    isSyncingRef.current = true;
    setIsSyncing(true);
    try {
      await syncAll();
    } catch {
      // Non-fatal: reference data may be partially cached
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, []);

  // ── Sync queue replay ─────────────────────────────────────────────────────

  const triggerQueueReplay = useCallback(async () => {
    // Ref guard (not state) so concurrent triggers — reconnect, periodic net,
    // token refresh, manual button — collapse into a single in-flight replay.
    if (isProcessingQueueRef.current) {return;}
    const initialCount = await getPendingCount();
    if (initialCount === 0) {return;}

    isProcessingQueueRef.current = true;
    setIsProcessingQueue(true);
    setSyncNotification({
      message: '',
      severity: 'info',
      i18nKey: 'network.syncInProgress',
      i18nValues: { count: initialCount },
    });

    let totalSuccess = 0;
    let totalFailed = 0;
    let authRequired = false;

    try {
      // Keep replaying until the queue is drained or we hit a stop condition.
      // processQueue drains everything reachable in one pass; extra passes pick
      // up items the user added while a sync was already running.
      for (let pass = 0; pass < MAX_REPLAY_PASSES; pass++) {
        if (!navigator.onLine) {break;}

        const result = await processQueue();
        totalSuccess += result.success;
        totalFailed += result.failed;
        setPendingSyncCount(result.remaining);

        if (result.authRequired) {
          authRequired = true;
          break;
        }
        if (result.networkLost) {break;}
        if (result.remaining === 0) {break;}
        // No progress this pass → stop to avoid spinning on persistently
        // failing items; the periodic safety net will retry them later.
        if (result.success === 0 && result.failed === 0) {break;}

        await delay(800);
      }

      needsReauthRef.current = authRequired;

      if (authRequired) {
        setSyncNotification({
          message: '',
          severity: 'warning',
          i18nKey: 'network.syncAuthRequired',
        });
      } else if (totalSuccess > 0) {
        setSyncNotification({
          message: '',
          severity: totalFailed > 0 ? 'warning' : 'success',
          i18nKey: 'network.syncCompleted',
          i18nValues: { success: totalSuccess, failed: totalFailed },
        });
      } else if (totalFailed > 0) {
        setSyncNotification({
          message: '',
          severity: 'error',
          i18nKey: 'network.syncFailed',
          i18nValues: { failed: totalFailed },
        });
      }
      // Nothing processed (e.g. network dropped immediately) → stay silent;
      // auto-sync will resume on its own.
    } catch {
      setSyncNotification({
        message: '',
        severity: 'error',
        i18nKey: 'network.syncError',
      });
    } finally {
      isProcessingQueueRef.current = false;
      setIsProcessingQueue(false);
    }
  }, []);

  // ── Poll pending count ────────────────────────────────────────────────────
  // Lightweight polling so the badge stays up-to-date without complex event wiring.

  useEffect(() => {
    let cancelled = false;

    async function refreshCount() {
      if (cancelled) {return;}
      try {
        const count = await getPendingCount();
        if (!cancelled) {setPendingSyncCount(count);}
      } catch {
        // IndexedDB unavailable — ignore
      }
    }

    void refreshCount();
    const intervalId = setInterval(() => void refreshCount(), 3_000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  // ── Listen for SYNC_QUEUE_RESULT messages from the Service Worker ─────────
  // The SW replays the queue independently (even when the app is closed).
  // When an open tab receives the result, update the count and show a toast.

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {return;}

    const handleSwMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; success?: number; failed?: number };
      if (data?.type !== 'SYNC_QUEUE_RESULT') {return;}

      const { success = 0, failed = 0 } = data;

      // Refresh the pending badge
      void getPendingCount().then(setPendingSyncCount);

      // Show a summary notification only if something was actually processed
      if (success > 0 || failed > 0) {
        setSyncNotification({
          message: '',
          severity: failed > 0 ? 'warning' : 'success',
          i18nKey: 'network.syncCompleted',
          i18nValues: { success, failed },
        });
      }
    };

    navigator.serviceWorker.addEventListener('message', handleSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSwMessage);
    };
  }, []);

  // ── Initial sync on first mount ───────────────────────────────────────────

  const initialSyncDone = useRef(false);
  useEffect(() => {
    if (isOnline && !initialSyncDone.current) {
      initialSyncDone.current = true;
      void triggerSync();
      // Drain any backlog left from a previous offline session right away.
      void triggerQueueReplay();
    }
  }, [isOnline, triggerSync, triggerQueueReplay]);

  // ── Reconnect: sync reference data + replay queue ─────────────────────────
  // Triggered by REAL reachability (browser online AND backend ping succeeds),
  // not just navigator.onLine — which is true even on Wi-Fi-without-internet
  // and would otherwise fire replays that immediately fail.

  useEffect(() => {
    const reachable = isOnline && isApiReachable;
    const wasReachable = prevReachableRef.current;
    prevReachableRef.current = reachable;

    if (reachable && !wasReachable && initialSyncDone.current) {
      void triggerSync();
      if (!needsReauthRef.current) {void triggerQueueReplay();}
    }
  }, [isOnline, isApiReachable, triggerSync, triggerQueueReplay]);

  // ── Auto-sync safety net ──────────────────────────────────────────────────
  // While online with a non-empty queue, keep retrying on a timer so the queue
  // drains on its own ("continue jusqu'à synchronisation complète") even if a
  // reconnect event was missed. Suppressed while a re-auth is required.

  useEffect(() => {
    const id = setInterval(() => {
      if (
        navigator.onLine &&
        !isProcessingQueueRef.current &&
        !needsReauthRef.current &&
        pendingCountRef.current > 0
      ) {
        void triggerQueueReplay();
      }
    }, AUTO_SYNC_INTERVAL_MS);

    return () => clearInterval(id);
  }, [triggerQueueReplay]);

  // ── Resume after re-authentication ────────────────────────────────────────
  // When tokens change (login or a successful refresh), clear the re-auth block
  // and immediately attempt to drain any backlog that was waiting on auth.

  useEffect(() => {
    const unsubscribe = onTokensChanged(() => {
      // Mode-aware on purpose. This used to test getAccessToken(), which is
      // always null in cookie mode — auto-sync would then never resume after a
      // reconnection, silently, with the agent's checkpoint records stuck in
      // the queue and nothing on screen to say so.
      const authenticated = hasSession();
      needsReauthRef.current = !authenticated;
      if (authenticated && navigator.onLine && pendingCountRef.current > 0) {
        void triggerQueueReplay();
      }
    });
    return unsubscribe;
  }, [triggerQueueReplay]);

  // ─────────────────────────────────────────────────────────────────────────

  const contextValue = useMemo<NetworkContextType>(
    () => ({
      isOnline,
      wasOffline,
      lastOnlineAt,
      networkQuality,
      effectiveType,
      pingMs,
      isApiReachable,
      pendingSyncCount,
      isSyncing,
      isProcessingQueue,
      syncNotification,
      clearSyncNotification,
      triggerSync,
      triggerQueueReplay,
    }),
    [
      isOnline,
      wasOffline,
      lastOnlineAt,
      networkQuality,
      effectiveType,
      pingMs,
      isApiReachable,
      pendingSyncCount,
      isSyncing,
      isProcessingQueue,
      syncNotification,
      clearSyncNotification,
      triggerSync,
      triggerQueueReplay,
    ]
  );

  return (
    <NetworkContext.Provider value={contextValue}>
      {children}
    </NetworkContext.Provider>
  );
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useNetwork() {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
}

export { NetworkContext };
